<template>
	<div class="rounded-lg border border-gray-200 overflow-hidden">
		<header class="p-4 border-b border-gray-200 bg-gray-50">
			<div class="flex items-start justify-between gap-3">
				<div>
					<p class="text-sm text-gray-600 mt-1">Assessment result summary</p>
				</div>
				<div
					class="px-3 py-1 rounded-full text-sm font-semibold"
					:class="isPassed ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'"
				>
					{{ isPassed ? 'Passed' : 'Failed' }}
				</div>
			</div>
		</header>

		<section class="p-4 space-y-4">
			<div class="grid grid-cols-1 md:grid-cols-5 gap-3">
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Score</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ percentage }}%</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Correct</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ correctAnswers }} / {{ totalQuestions }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Points</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ formatPoints(earnedPoints) }} / {{ formatPoints(totalPoints) }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Duration</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ formattedDuration }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Retakes</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ retakeCount }}</div>
				</div>
			</div>

			<div>
				<div class="flex items-center justify-between text-xs text-gray-600 mb-1">
					<span>Passing Score: {{ passingPercent }}%</span>
				</div>
				<div class="h-2 bg-gray-200 rounded-full overflow-hidden">
					<div class="h-2 transition-all duration-300" :class="isPassed ? 'bg-success' : 'bg-danger'" :style="{ width: `${percentage}%` }"></div>
				</div>
			</div>

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Criteria-based Points
				</div>
				<div class="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Competency
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in competencyScores"
								:key="`comp-${item.label}`"
								class="flex items-center justify-between text-xs text-gray-700"
							>
								<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
								<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
							</div>
							<div v-if="competencyScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>

					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Type of Question
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in questionTypeScores"
								:key="`type-${item.label}`"
								class="flex items-center justify-between text-xs text-gray-700"
							>
								<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
								<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
							</div>
							<div v-if="questionTypeScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>

					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Critical Level
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in criticalLevelScores"
								:key="`critical-${item.label}`"
								class="flex items-center justify-between text-xs text-gray-700"
							>
								<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
								<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
							</div>
							<div v-if="criticalLevelScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>
				</div>
			</div>

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Attempt History
				</div>
				<div class="p-3 space-y-2">
					<div class="text-xs text-gray-600">
						Total attempts: <strong>{{ attemptHistory.length }}</strong> | Retakes: <strong>{{ retakeCount }}</strong>
					</div>
					<div class="max-h-[180px] overflow-y-auto border border-gray-200 rounded">
						<div
							v-for="attempt in attemptHistory"
							:key="`attempt-${attempt.attempt}`"
							class="px-3 py-2 border-b border-gray-100 last:border-b-0 text-xs text-gray-700 flex items-center justify-between gap-3"
						>
							<div class="font-semibold">Attempt {{ attempt.attempt }}</div>
							<div>Score: {{ attempt.percentage }}%</div>
							<div>Points: {{ formatPoints(attempt.earnedPoints) }} / {{ formatPoints(attempt.totalPoints) }}</div>
							<div>Correct: {{ attempt.correctAnswers }} / {{ attempt.totalQuestions }}</div>
							<div>{{ formatDuration(attempt.elapsedSeconds) }}</div>
						</div>
						<div v-if="attemptHistory.length === 0" class="px-3 py-2 text-xs text-gray-500">No attempts yet</div>
					</div>
				</div>
			</div>

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Question Breakdown
				</div>
				<div class="max-h-[280px] overflow-y-auto">
					<div
						v-for="result in results"
						:key="result.questionNumber"
						class="px-3 py-3 border-b border-gray-100 last:border-b-0"
					>
						<div class="flex items-start justify-between gap-3">
							<div>
								<div class="text-sm font-semibold text-gray-800">
									Q{{ result.questionNumber }}. {{ result.questionText }}
								</div>
								<div class="text-xs text-gray-600 mt-1">
									<strong>Selected: {{ result.selectedAnswerLabels.length ? result.selectedAnswerLabels.join(', ') : 'No answer' }}</strong>
								</div>
								<div class="text-xs text-gray-600 mt-1">
									Correct: {{ result.correctAnswerLabels.join(', ') }}
								</div>
								<div class="text-xs text-gray-600 mt-1">
									<strong>Competency: {{ result.competency }} | Type: {{ result.questionType }} | Critical: {{ result.criticalLevel }}</strong>
								</div>
								<div v-if="result.explanation" class="text-xs text-gray-600 mt-1">
									{{ result.explanation }}
								</div>
							</div>
							<div class="text-right shrink-0">
								<div
									class="text-xs font-semibold px-2 py-1 rounded-full"
									:class="result.isCorrect ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'"
								>
									{{ result.isCorrect ? 'Correct' : 'Incorrect' }}
								</div>
								<div class="text-xs text-gray-600 mt-2">
									{{ formatPoints(result.pointsEarned) }} / {{ formatPoints(result.pointsPossible) }} pts
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<footer class="p-4 border-t border-gray-200 flex justify-end gap-2">
			<EwButton label="Retake" type="secondary" icon="tabler:rotate-clockwise" @click="$emit('retake')" />
			<EwButton label="Close" type="success" iconAppend="tabler:check" @click="$emit('close')" />
		</footer>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type EvaluationResult = {
	questionNumber: number;
	questionText: string;
	isCorrect: boolean;
	basePoints: number;
	pointsEarned: number;
	pointsPossible: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
	selectedAnswerLabels: string[];
	correctAnswerLabels: string[];
	explanation: string;
};

type CriteriaScoreItem = {
	label: string;
	earnedPoints: number;
	totalPoints: number;
	questionCount: number;
};

type AttemptHistoryItem = {
	attempt: number;
	percentage: number;
	earnedPoints: number;
	totalPoints: number;
	correctAnswers: number;
	totalQuestions: number;
	elapsedSeconds: number;
	createdAt: string;
};

const props = defineProps<{
	title: string;
	totalQuestions: number;
	answeredQuestions: number;
	correctAnswers: number;
	totalPoints: number;
	earnedPoints: number;
	percentage: number;
	elapsedSeconds: number;
	passingPercent: number;
	results: EvaluationResult[];
	competencyScores: CriteriaScoreItem[];
	questionTypeScores: CriteriaScoreItem[];
	criticalLevelScores: CriteriaScoreItem[];
	attemptHistory: AttemptHistoryItem[];
	retakeCount: number;
}>();

defineEmits<{
	retake: [];
	close: [];
}>();

const isPassed = computed(() => props.percentage >= props.passingPercent);

const grade = computed(() => {
	if (props.percentage >= 90) return 'A';
	if (props.percentage >= 80) return 'B';
	if (props.percentage >= 70) return 'C';
	if (props.percentage >= 60) return 'D';
	return 'F';
});

const formattedDuration = computed(() => {
	const minutes = Math.floor(props.elapsedSeconds / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(props.elapsedSeconds % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
});

const formatDuration = (elapsed: number): string => {
	const minutes = Math.floor(elapsed / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(elapsed % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
};

const formatPoints = (value: number): string => {
	if (!Number.isFinite(value)) {
		return '0';
	}
	return Number.isInteger(value) ? String(value) : value.toFixed(2);
};
</script>
